import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createSelaProject,
  type SelaDealContextDates,
  type SelaDealStage,
  type SelaDevelopmentPath,
  type SelaPriority,
} from '../../services/selaApi';
import {
  PARCEL_MODE_SUPPORTED_CITIES,
  type AddressGeocode,
  type AddressSuggestion,
  fetchAddressSuggestions,
  geocodeAddressLabel,
  getSupportedCitiesLabel,
  isSupportedCityState,
  isSupportedParcelCity,
  parseCityStateFromAddress,
} from '../../utils/addressSuggestions';
import { useRumStepDwell } from '../../utils/rum';
import {
  buildOnboardingDraft,
  buildOnboardingSearch,
  clearV2OnboardingState,
  persistV2OnboardingState,
  readV2OnboardingState,
} from '../../utils/v2Onboarding';
import {
  ONBOARDING_STEP_NAMES,
  recordV2ErrorStage,
  recordV2FunnelStep,
  recordV2Latency,
} from '../../utils/v2Rum';
import { OnboardingStepMapPanel } from './OnboardingStepMapPanel';

type LocationMode = 'address' | 'parcel';
type OnboardingStep = 1 | 2 | 3 | 4;

type SelaProjectOnboardingFlowProps = {
  initialAddress?: string;
  initialLocationMode?: LocationMode;
  initialParcelPin?: string;
  initialCity?: string;
  initialState?: string;
  isAuthenticated?: boolean;
  onExit: () => void;
};

const DEVELOPMENT_PATH_OPTIONS: Array<{
  key: SelaDevelopmentPath;
  title: string;
  description: string;
}> = [
  {
    key: 'adu_dadu',
    title: 'ADU / DADU',
    description: 'Backyard cottage, garage conversion, attached ADU, or rental unit.',
  },
  {
    key: 'remodel_addition',
    title: 'Remodel / Addition',
    description: 'Expand, convert, or improve the existing structure.',
  },
  {
    key: 'scrape_rebuild',
    title: 'Scrape & Rebuild',
    description: 'Demolish the existing structure and build new.',
  },
  {
    key: 'single_family_home',
    title: 'Single Family Home',
    description: 'Evaluate a detached home path, whether preserving, replacing, or building new.',
  },
  {
    key: 'small_multifamily_townhomes',
    title: 'Small Multifamily / Townhomes',
    description: 'Duplex, triplex, fourplex, townhomes, cottage housing, or similar infill housing.',
  },
  {
    key: 'short_plat_subdivision',
    title: 'Short Plat / Subdivision',
    description: 'Split the parcel or evaluate multiple buildable lots.',
  },
  {
    key: 'buy_hold_rental',
    title: 'Buy & Hold / Rental',
    description: 'Keep the existing structure and evaluate rental, appreciation, or light value-add upside.',
  },
  {
    key: 'recommend_paths',
    title: 'Not sure - recommend paths',
    description: 'Let Sela infer likely development paths from zoning, lot context, overlays, permits, and site constraints.',
  },
];

const DEAL_STAGE_OPTIONS: Array<{
  key: SelaDealStage;
  title: string;
  description: string;
}> = [
  {
    key: 'early_screening',
    title: 'Early screening',
    description: "I'm deciding whether this parcel is worth further attention.",
  },
  {
    key: 'considering_offer',
    title: 'Considering an offer',
    description: 'I need to understand risks before making a bid.',
  },
  {
    key: 'under_contract',
    title: 'Under contract / feasibility period',
    description: 'I have deadlines and need to verify key risks before removing contingencies.',
  },
  {
    key: 'parcel_controlled',
    title: 'Parcel controlled / owned',
    description: 'I already control the parcel and want to move toward permit readiness.',
  },
  {
    key: 'active_predevelopment',
    title: 'Active predevelopment',
    description: "I'm coordinating consultants, documents, permits, and decisions.",
  },
];

const PRIORITY_OPTIONS: Array<{
  key: SelaPriority;
  title: string;
}> = [
  { key: 'fastest_permit_path', title: 'Fastest permit path' },
  { key: 'max_buildable_units', title: 'Max buildable units' },
  { key: 'lowest_entitlement_risk', title: 'Lowest entitlement risk' },
  { key: 'lowest_construction_complexity', title: 'Lowest construction complexity' },
  { key: 'best_resale_upside', title: 'Best resale upside' },
  { key: 'best_rental_income', title: 'Best rental income' },
  { key: 'avoid_environmental_site_surprises', title: 'Avoid environmental/site surprises' },
];

function buildProjectName(address: string): string {
  const street = address.split(',')[0]?.trim() || address.trim();
  return `${street} Parcel Evaluation`.trim();
}

function buildParcelProjectName(
  parcelPin: string,
  city: string,
  state: string,
): string {
  const cityState = [city, state.toUpperCase()].filter(Boolean).join(', ');
  const head = cityState ? `Parcel ${parcelPin} (${cityState})` : `Parcel ${parcelPin}`;
  return `${head} Evaluation`.trim();
}

function formatCoverageLabel(selectedAddress: string | null) {
  if (!selectedAddress) {
    return 'Choose a verified address to continue';
  }

  const { city, state } = parseCityStateFromAddress(selectedAddress);
  if (!city || !state) {
    return 'We will confirm zoning and parcel context during onboarding';
  }

  return isSupportedCityState(city, state)
    ? `Coverage available in ${city}, ${state}`
    : `Coverage preview for ${city}, ${state}`;
}

function getStateBadgeLabel(selectedAddress: string | null) {
  const { state } = parseCityStateFromAddress(selectedAddress || '');
  return state || 'Pending';
}

function getCityBadgeLabel(selectedAddress: string | null) {
  const { city } = parseCityStateFromAddress(selectedAddress || '');
  return city || 'Select address';
}

export function SelaProjectOnboardingFlow({
  initialAddress = '',
  initialLocationMode,
  initialParcelPin = '',
  initialCity = '',
  initialState = '',
  isAuthenticated = true,
  onExit,
}: SelaProjectOnboardingFlowProps) {
  const navigate = useNavigate();
  const initialMode: LocationMode = initialLocationMode || (initialParcelPin ? 'parcel' : 'address');
  const hasInitialParcel = initialMode === 'parcel' && Boolean(initialParcelPin);
  const [locationMode, setLocationMode] = useState<LocationMode>(initialMode);
  const [step, setStep] = useState<OnboardingStep>(initialAddress || hasInitialParcel ? 2 : 1);
  const [address, setAddress] = useState(initialAddress);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(initialAddress || null);
  const [selectedGeocode, setSelectedGeocode] = useState<AddressGeocode | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [coverageWarning, setCoverageWarning] = useState<string | null>(null);
  const [parcelPin, setParcelPin] = useState(initialParcelPin);
  const defaultParcelCity = PARCEL_MODE_SUPPORTED_CITIES[0];
  const initialParcelKey =
    PARCEL_MODE_SUPPORTED_CITIES.find(
      (entry) =>
        entry.city === (initialCity || '').trim().toLowerCase() &&
        entry.state === (initialState || '').trim().toLowerCase(),
    )?.label || defaultParcelCity.label;
  const [parcelCityKey, setParcelCityKey] = useState<string>(initialParcelKey);
  const [developmentPaths, setDevelopmentPaths] = useState<SelaDevelopmentPath[]>([]);
  const [dealStage, setDealStage] = useState<SelaDealStage>('early_screening');
  const [feasibilityDeadline, setFeasibilityDeadline] = useState('');
  const [closingDate, setClosingDate] = useState('');
  const [targetPermitSubmission, setTargetPermitSubmission] = useState('');
  const [targetGroundbreak, setTargetGroundbreak] = useState('');
  const [priorities, setPriorities] = useState<SelaPriority[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasTrackedFlowEnterRef = useRef(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const selectedParcelCity = useMemo(
    () =>
      PARCEL_MODE_SUPPORTED_CITIES.find((entry) => entry.label === parcelCityKey) || defaultParcelCity,
    [parcelCityKey, defaultParcelCity],
  );

  const parcelReady = useMemo(() => {
    if (locationMode !== 'parcel') return false;
    const trimmed = parcelPin.trim();
    if (!trimmed) return false;
    return isSupportedParcelCity(selectedParcelCity.city, selectedParcelCity.state);
  }, [locationMode, parcelPin, selectedParcelCity]);

  const selectedCoverage = useMemo(() => {
    if (!selectedSuggestion) {
      return { isSupported: false, city: undefined as string | undefined, state: undefined as string | undefined };
    }
    const { city, state } = parseCityStateFromAddress(selectedSuggestion);
    return {
      isSupported: isSupportedCityState(city, state),
      city,
      state,
    };
  }, [selectedSuggestion]);

  const stepOneReady = locationMode === 'address'
    ? Boolean(selectedSuggestion) && selectedCoverage.isSupported
    : parcelReady;

  const selectedDevelopmentPathOptions = useMemo(
    () => DEVELOPMENT_PATH_OPTIONS.filter((option) => developmentPaths.includes(option.key)),
    [developmentPaths],
  );

  const selectedDealStage = useMemo(
    () => DEAL_STAGE_OPTIONS.find((option) => option.key === dealStage) || DEAL_STAGE_OPTIONS[0],
    [dealStage],
  );

  const selectedPriorityOptions = useMemo(
    () => PRIORITY_OPTIONS.filter((option) => priorities.includes(option.key)),
    [priorities],
  );

  const dealContextDates = useMemo<SelaDealContextDates>(() => {
    const dates: SelaDealContextDates = {};
    if (dealStage === 'under_contract') {
      if (feasibilityDeadline) dates.feasibilityDeadline = feasibilityDeadline;
      if (closingDate) dates.closingDate = closingDate;
    }
    if (dealStage === 'parcel_controlled' || dealStage === 'active_predevelopment') {
      if (targetPermitSubmission) dates.targetPermitSubmission = targetPermitSubmission;
      if (targetGroundbreak) dates.targetGroundbreak = targetGroundbreak;
    }
    return dates;
  }, [closingDate, dealStage, feasibilityDeadline, targetGroundbreak, targetPermitSubmission]);

  const stepDwellMetadata = {
    location_mode: locationMode,
    prefilled: Boolean(initialAddress || hasInitialParcel),
  };
  useRumStepDwell(ONBOARDING_STEP_NAMES[step], step, stepDwellMetadata);

  useEffect(() => {
    if (hasTrackedFlowEnterRef.current) {
      return;
    }
    hasTrackedFlowEnterRef.current = true;
    recordV2FunnelStep('v2_public_property', 'shown', {
      initial_step: step,
      prefilled: Boolean(initialAddress || hasInitialParcel),
      location_mode: locationMode,
      flow: 'onboarding_enter',
    });
  }, [hasInitialParcel, initialAddress, locationMode, step]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const draft = readV2OnboardingState();
    if (!draft || draft.pendingCreate) {
      return;
    }
    if (draft.developmentPaths?.length) {
      setDevelopmentPaths(draft.developmentPaths);
    }
    if (draft.dealStage) {
      setDealStage(draft.dealStage);
    }
    if (draft.priorities?.length) {
      setPriorities(draft.priorities);
    }
    const dates = draft.dealContextDates || {};
    if (dates.feasibilityDeadline) {
      setFeasibilityDeadline(dates.feasibilityDeadline);
    }
    if (dates.closingDate) {
      setClosingDate(dates.closingDate);
    }
    if (dates.targetPermitSubmission) {
      setTargetPermitSubmission(dates.targetPermitSubmission);
    }
    if (dates.targetGroundbreak) {
      setTargetGroundbreak(dates.targetGroundbreak);
    }
    const hasLocation = Boolean(initialAddress || hasInitialParcel);
    if (hasLocation && draft.developmentPaths?.length) {
      setStep(draft.priorities?.length ? 4 : 3);
    }
  }, [hasInitialParcel, initialAddress, isAuthenticated]);

  const handleExit = () => {
    recordV2FunnelStep('v2_onboarding_abandon', 'abandon', {
      last_step: step,
      location_mode: locationMode,
    });
    onExit();
  };

  const buildCurrentDraft = (pendingCreate = false) =>
    buildOnboardingDraft({
      locationMode,
      address: (selectedSuggestion || address).trim() || undefined,
      parcelPin: parcelPin.trim() || undefined,
      city: selectedParcelCity.city,
      state: selectedParcelCity.state.toUpperCase(),
      developmentPaths,
      dealStage,
      priorities,
      dealContextDates,
      pendingCreate,
    });

  const handleAuthNavigate = (path: '/login' | '/signup', authPath?: string) => {
    const draft = buildCurrentDraft(false);
    recordV2FunnelStep('v2_onboarding_continue_auth', 'click', {
      auth_path: authPath || (path === '/signup' ? 'signup' : 'login'),
      location_mode: locationMode,
      funnel_step_index: step,
    });
    try {
      persistV2OnboardingState(draft);
      recordV2FunnelStep('v2_onboarding_state_persist', 'success', { location_mode: locationMode });
    } catch {
      recordV2FunnelStep('v2_onboarding_state_persist', 'failure', { location_mode: locationMode });
    }
    navigate(`${path}${buildOnboardingSearch(draft)}`);
  };

  const handleCreateProject = async () => {
    const startedAt = performance.now();
    recordV2FunnelStep('v2_onboarding_step_priorities', 'success', {
      priority_count: priorities.length,
    });
    recordV2FunnelStep('v2_onboarding_project_create', 'start', {
      location_mode: locationMode,
      deal_stage: dealStage,
      path_count: developmentPaths.length,
      priority_count: priorities.length,
    });

    if (!isAuthenticated) {
      const draft = buildCurrentDraft(true);
      recordV2FunnelStep('v2_onboarding_continue_auth', 'click', {
        auth_path: 'signup_after_priorities',
        location_mode: locationMode,
        funnel_step_index: 4,
      });
      try {
        persistV2OnboardingState(draft);
        recordV2FunnelStep('v2_onboarding_state_persist', 'success', {
          location_mode: locationMode,
          pending_create: true,
        });
      } catch {
        recordV2FunnelStep('v2_onboarding_state_persist', 'failure', { location_mode: locationMode });
        setError('Unable to save your selections. Please try again.');
        return;
      }
      navigate(`/signup${buildOnboardingSearch(draft)}`);
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const trimmedPin = parcelPin.trim();
      const createPayload = {
        projectType: 'residential',
        evaluationMode: 'parcel_evaluation' as const,
        developmentPaths,
        dealStage,
        priorities,
        dealContextDates,
      };
      const project =
        locationMode === 'parcel'
          ? await createSelaProject({
              projectName: buildParcelProjectName(
                trimmedPin,
                selectedParcelCity.city,
                selectedParcelCity.state,
              ),
              ...createPayload,
              locationMode: 'parcel',
              parcelPin: trimmedPin,
              city: selectedParcelCity.city,
              state: selectedParcelCity.state.toUpperCase(),
            })
          : await createSelaProject({
              projectName: buildProjectName(selectedSuggestion || address),
              ...createPayload,
              locationMode: 'address',
              propertyAddress: (selectedSuggestion || address).trim(),
            });
      recordV2FunnelStep('v2_onboarding_project_create', 'success', {
        project_id: project.id,
        location_mode: locationMode,
        deal_stage: dealStage,
        path_count: developmentPaths.length,
      });
      recordV2Latency('v2_onboarding_project_create', performance.now() - startedAt);
      recordV2FunnelStep('v2_onboarding_workspace_enter', 'success', {
        project_id: project.id,
        start_project_research: true,
      });
      clearV2OnboardingState();
      navigate(`/project/${project.id}`, {
        state: { startProjectResearch: true },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project.');
      recordV2FunnelStep('v2_onboarding_project_create', 'failure', {
        location_mode: locationMode,
        deal_stage: dealStage,
      });
      recordV2ErrorStage('v2_onboarding_project_create', err);
    } finally {
      setCreating(false);
    }
  };

  const toggleDevelopmentPath = (path: SelaDevelopmentPath) => {
    setDevelopmentPaths((current) =>
      current.includes(path) ? current.filter((item) => item !== path) : [...current, path],
    );
  };

  const togglePriority = (priority: SelaPriority) => {
    setPriorities((current) => {
      if (current.includes(priority)) {
        return current.filter((item) => item !== priority);
      }
      if (current.length >= 3) {
        return current;
      }
      return [...current, priority];
    });
  };

  useEffect(() => {
    if (initialAddress) {
      const { city, state } = parseCityStateFromAddress(initialAddress);
      if (!isSupportedCityState(city, state)) {
        setCoverageWarning(
          `Coverage not available yet for ${city || 'this city'}, ${state || 'this state'}. Supported cities: ${getSupportedCitiesLabel()}.`,
        );
      }
    }
  }, [initialAddress]);

  useEffect(() => {
    if (!initialAddress || locationMode !== 'address') return undefined;
    const controller = new AbortController();
    geocodeAddressLabel(initialAddress, controller.signal)
      .then((geocode) => {
        if (geocode) setSelectedGeocode(geocode);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [initialAddress, locationMode]);

  useEffect(() => {
    if (address.trim().length < 1 || selectedSuggestion) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const nextSuggestions = await fetchAddressSuggestions(address.trim(), controller.signal);
        setSuggestions(nextSuggestions);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, address.trim().length === 1 ? 500 : 900);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      controller.abort();
    };
  }, [address, selectedSuggestion]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (selectedSuggestion) {
      setSelectedSuggestion(null);
      setSelectedGeocode(null);
      setCoverageWarning(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setAddress(value);
    setSelectedIndex(-1);
    if (value.trim().length >= 1) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    setAddress(suggestion.label);
    setSelectedSuggestion(suggestion.label);
    setSelectedGeocode({ lat: suggestion.lat, lng: suggestion.lng, label: suggestion.label });
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    const { city, state } = parseCityStateFromAddress(suggestion.label);
    if (!isSupportedCityState(city, state)) {
      setCoverageWarning(
        `Coverage not available yet for ${city || 'this city'}, ${state || 'this state'}. Supported cities: ${getSupportedCitiesLabel()}.`,
      );
    } else {
      setCoverageWarning(null);
    }
    inputRef.current?.blur();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f9fb]">
      <div className="border-b border-[#e5ebf2] bg-white">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-10">
          <button
            type="button"
            onClick={handleExit}
            className="inline-flex items-center gap-2 rounded-md text-left text-[#101830] transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-[#1abf7d]" />
            <span className="text-[22px] font-bold tracking-[-0.03em]">Sela</span>
          </button>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleExit}
              className="rounded text-sm font-medium text-[#808fa6] transition-colors hover:text-[#101830] focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
            >
              Save & exit
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleAuthNavigate('/login', 'header_login')}
                className="rounded-full border border-[#dbe3ec] bg-white px-4 py-2 text-sm font-medium text-[#4c617b] transition-colors hover:border-[#b7c5d6] hover:text-[#101830] focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
              >
                Log in
              </button>
              <button
                type="button"
                onClick={() => handleAuthNavigate('/signup', 'header_signup')}
                className="rounded-full bg-[#101830] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#18233f] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
              >
                Create account
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 py-8 md:px-10">
        <div className="flex flex-wrap items-center justify-center gap-3 text-[13px]">
          {[
            { number: 1, label: 'Parcel' },
            { number: 2, label: 'Development Paths' },
            { number: 3, label: 'Deal Context' },
            { number: 4, label: 'Priorities' },
          ].map((item, index) => (
            <React.Fragment key={item.number}>
              {index > 0 ? <div className="h-px w-10 bg-[#ccd4de]" /> : null}
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                    step >= item.number ? 'bg-[#1abf7d] text-white' : 'bg-[#dbe0e8] text-[#808fa6]'
                  }`}
                >
                  {item.number}
                </span>
                <span className={step === item.number ? 'font-semibold text-[#101830]' : 'text-[#99a6b8]'}>
                  {item.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {step === 1 ? (
          <div className="mt-10 grid gap-8 lg:grid-cols-[540px_minmax(0,1fr)]">
            <section className="flex min-h-[680px] flex-col rounded-2xl bg-white p-8 shadow-[0px_24px_64px_rgba(16,24,48,0.08)] sm:p-9">
              <div className="max-w-[470px]">
                <p className="inline-flex rounded-full border border-[#d7ecdf] bg-[#edf9f2] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f8a5b]">
                  Predevelopment onboarding
                </p>
                <h1 className="mt-5 text-[34px] font-bold tracking-[-0.04em] text-[#101830]">Which parcel are you evaluating?</h1>
              </div>

              <div
                className="mt-6 inline-flex rounded-xl bg-[#eef2f7] p-1 text-sm font-semibold"
                role="tablist"
                aria-label="Location input mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={locationMode === 'address'}
                  onClick={() => {
                    setLocationMode('address');
                    setError(null);
                  }}
                  className={`rounded-lg px-4 py-2 transition-colors ${
                    locationMode === 'address'
                      ? 'bg-white text-[#101830] shadow-sm'
                      : 'text-[#5f7088] hover:text-[#101830]'
                  }`}
                >
                  Address
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={locationMode === 'parcel'}
                  onClick={() => {
                    setLocationMode('parcel');
                    setError(null);
                  }}
                  className={`rounded-lg px-4 py-2 transition-colors ${
                    locationMode === 'parcel'
                      ? 'bg-white text-[#101830] shadow-sm'
                      : 'text-[#5f7088] hover:text-[#101830]'
                  }`}
                >
                  Parcel Number
                </button>
              </div>

              {locationMode === 'parcel' ? (
                <div className="mt-8 space-y-5">
                  <div>
                    <label
                      htmlFor="v2-parcel-city"
                      className="mb-3 block text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8a98ad]"
                    >
                      Jurisdiction
                    </label>
                    <select
                      id="v2-parcel-city"
                      value={parcelCityKey}
                      onChange={(event) => setParcelCityKey(event.target.value)}
                      className="h-12 w-full rounded-xl border-2 border-[#d5dee9] bg-[#f7fafc] px-3 text-[15px] text-[#26334d] focus:border-[#1abf7d] focus:outline-none"
                    >
                      {PARCEL_MODE_SUPPORTED_CITIES.map((entry) => (
                        <option key={entry.label} value={entry.label}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-[#8a98ad]">
                      Parcel-number onboarding is available in Seattle today. More cities coming soon.
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="v2-parcel-pin"
                      className="mb-3 block text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8a98ad]"
                    >
                      Parcel number (APN / PIN)
                    </label>
                    <input
                      id="v2-parcel-pin"
                      type="text"
                      value={parcelPin}
                      onChange={(event) => setParcelPin(event.target.value)}
                      placeholder="e.g. 1234567890"
                      className="h-14 w-full rounded-xl border-2 border-[#d5dee9] bg-[#f7fafc] px-4 text-[15px] text-[#26334d] placeholder:text-[#94a3b8] focus:border-[#1abf7d] focus:outline-none"
                    />
                    <p className="mt-2 text-xs text-[#8a98ad]">
                      Use this for a subject parcel you are evaluating before an offer, during diligence, or before
                      permit-readiness work.
                    </p>
                  </div>
                </div>
              ) : null}

              <div className={locationMode === 'parcel' ? 'hidden' : 'mt-8'} ref={suggestionsRef}>
                <label htmlFor="v2-auth-address" className="mb-3 block text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8a98ad]">
                  Property address
                </label>
                <div
                  className={`relative rounded-xl border-2 bg-[#f7fafc] transition-colors ${
                    selectedSuggestion ? 'border-[#1abf7d]' : 'border-[#d5dee9] focus-within:border-[#1abf7d]'
                  }`}
                >
                  <div className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border border-[#8193ac]" />
                  <input
                    id="v2-auth-address"
                    ref={inputRef}
                    type="text"
                    value={address}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    readOnly={Boolean(selectedSuggestion)}
                    placeholder={selectedSuggestion ? 'Address selected - continue to development paths' : 'Type to search verified addresses...'}
                    className={`h-14 w-full rounded-xl bg-transparent pl-11 pr-24 text-[15px] text-[#26334d] placeholder:text-[#94a3b8] focus:outline-none ${
                      selectedSuggestion ? 'cursor-default' : ''
                    }`}
                  />
                  {selectedSuggestion ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSuggestion(null);
                        setSelectedGeocode(null);
                        setCoverageWarning(null);
                        setAddress('');
                        setSuggestions([]);
                        setShowSuggestions(false);
                        inputRef.current?.focus();
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#4c617b] shadow-sm ring-1 ring-[#d5dee9] transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                {showSuggestions && !selectedSuggestion ? (
                  <div className="mt-3 overflow-hidden rounded-2xl border border-[#d9e2ec] bg-white shadow-[0px_24px_48px_rgba(16,24,48,0.12)]">
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.label}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`flex w-full items-center gap-3 border-b border-[#edf1f5] px-4 py-3 text-left text-sm transition-colors last:border-b-0 ${
                            index === selectedIndex ? 'bg-[#edf9f2] text-[#101830]' : 'text-[#304154] hover:bg-slate-50'
                          }`}
                        >
                          <span className="h-2.5 w-2.5 rounded-full bg-[#1abf7d]" />
                          <span>{suggestion.label}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-sm text-[#708099]">Searching for addresses...</div>
                    )}
                  </div>
                ) : null}
              </div>

              {locationMode === 'address' ? (
                <>
                  <div className="mt-5 rounded-xl border border-[#99e5ba] bg-[#e8fcf0] px-4 py-3 text-sm font-medium text-[#0a4d2e]">
                    {formatCoverageLabel(selectedSuggestion)}
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                      <p className="text-[11px] text-[#808fa6]">Coverage</p>
                      <p className="mt-1 text-sm font-semibold text-[#101830]">
                        {selectedSuggestion ? (selectedCoverage.isSupported ? 'Supported' : 'Limited') : 'Pending'}
                      </p>
                    </div>
                    <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                      <p className="text-[11px] text-[#808fa6]">City</p>
                      <p className="mt-1 text-sm font-semibold text-[#101830]">{getCityBadgeLabel(selectedSuggestion)}</p>
                    </div>
                    <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                      <p className="text-[11px] text-[#808fa6]">State</p>
                      <p className="mt-1 text-sm font-semibold text-[#101830]">{getStateBadgeLabel(selectedSuggestion)}</p>
                    </div>
                  </div>

                  {coverageWarning ? (
                    <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                      {coverageWarning}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                    <p className="text-[11px] text-[#808fa6]">Mode</p>
                    <p className="mt-1 text-sm font-semibold text-[#101830]">Parcel number</p>
                  </div>
                  <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                    <p className="text-[11px] text-[#808fa6]">Jurisdiction</p>
                    <p className="mt-1 text-sm font-semibold text-[#101830]">{selectedParcelCity.label}</p>
                  </div>
                  <div className="rounded-xl bg-[#f5f7fc] px-4 py-3">
                    <p className="text-[11px] text-[#808fa6]">Parcel</p>
                    <p className="mt-1 text-sm font-semibold text-[#101830]">
                      {parcelPin.trim() || 'Pending'}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-auto pt-10">
                <p className="text-xs text-[#99a6b8]">Step 1 of 4 - subject parcel for downstream analysis</p>
                <button
                  type="button"
                  disabled={!stepOneReady}
                  onClick={() => {
                    recordV2FunnelStep('v2_onboarding_step_parcel', 'success', { location_mode: locationMode });
                    setStep(2);
                  }}
                  className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#101830] px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#c4ccd8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                >
                  Continue to Development Paths
                </button>
              </div>
            </section>

            <OnboardingStepMapPanel locationMode={locationMode} geocode={selectedGeocode} />
          </div>
        ) : step === 2 ? (
          <div className="mt-10">
            <div className="rounded-2xl border border-[#e6ebf2] bg-white p-6 shadow-[0px_18px_40px_rgba(16,24,48,0.06)]">
              <p className="text-sm font-medium text-[#708099]">Subject parcel</p>
              <p className="mt-2 text-lg font-semibold text-[#101830]">
                {locationMode === 'parcel'
                  ? `Parcel ${parcelPin.trim()} - ${selectedParcelCity.label}`
                  : selectedSuggestion || address}
              </p>
            </div>

            <h1 className="mt-10 text-[34px] font-bold tracking-[-0.04em] text-[#101830]">What development paths should Sela evaluate?</h1>
            <p className="mt-3 max-w-4xl text-[15px] leading-7 text-[#708099]">
              Choose one or more possible strategies. You can select &quot;Not sure&quot; and let Sela recommend likely paths.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {DEVELOPMENT_PATH_OPTIONS.map((option) => {
                const selected = developmentPaths.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => toggleDevelopmentPath(option.key)}
                    className={`rounded-2xl border p-6 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2 ${
                      selected
                        ? 'border-[#1abf7d] bg-[#f0fbf5] shadow-[0px_12px_24px_rgba(26,191,125,0.12)]'
                        : 'border-[#e6ebf2] bg-white hover:border-[#c7d4e2] hover:shadow-[0px_12px_24px_rgba(16,24,48,0.06)]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="text-xl font-semibold text-[#101830]">{option.title}</h2>
                      <span className={`mt-1 h-5 w-5 rounded border ${selected ? 'border-[#1abf7d] bg-[#1abf7d]' : 'border-[#c7d4e2] bg-white'}`} />
                    </div>
                    <p className="mt-3 text-sm leading-6 text-[#5f7088]">{option.description}</p>
                    {selected ? <p className="mt-5 text-sm font-medium text-[#0f8a5b]">Selected for evaluation</p> : null}
                  </button>
                );
              })}
            </div>

            {developmentPaths.length === 0 ? (
              <p className="mt-4 text-sm text-[#8a98ad]">Select at least one path, or choose &quot;Not sure - recommend paths&quot;.</p>
            ) : null}

            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-lg border border-[#d9e2ec] bg-white px-4 py-2.5 text-sm font-medium text-[#526b84] transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
              >
                Back
              </button>
              <button
                type="button"
                disabled={developmentPaths.length === 0}
                onClick={() => {
                  recordV2FunnelStep('v2_onboarding_step_development_paths', 'success', {
                    path_count: developmentPaths.length,
                    paths: developmentPaths,
                  });
                  setStep(3);
                }}
                className="rounded-lg bg-[#101830] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#c4ccd8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
              >
                Continue to Deal Context
              </button>
            </div>
          </div>
        ) : step === 3 ? (
          <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
            <section className="rounded-2xl bg-white p-8 shadow-[0px_18px_40px_rgba(16,24,48,0.06)]">
              <h1 className="text-[32px] font-bold tracking-[-0.04em] text-[#101830]">Where are you in the process?</h1>
              <p className="mt-3 max-w-3xl text-[15px] leading-7 text-[#708099]">
                Sela will use this to prioritize the right diligence steps, risks, deadlines, and next actions.
              </p>

              <div className="mt-8 space-y-3">
                {DEAL_STAGE_OPTIONS.map((option) => {
                  const selected = option.key === dealStage;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setDealStage(option.key)}
                      className={`w-full rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2 ${
                        selected ? 'border-[#1abf7d] bg-[#f0fbf5]' : 'border-[#e6ebf2] bg-white hover:border-[#c7d4e2]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2 className="text-lg font-semibold text-[#101830]">{option.title}</h2>
                          <p className="mt-2 text-sm leading-6 text-[#5f7088]">{option.description}</p>
                        </div>
                        <span className={`mt-1 h-4 w-4 rounded-full ${selected ? 'bg-[#1abf7d]' : 'bg-[#dbe0e8]'}`} />
                      </div>
                    </button>
                  );
                })}
              </div>

              {dealStage === 'under_contract' ? (
                <div className="mt-7 grid gap-4 rounded-2xl bg-[#f7fafc] p-5 md:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-medium text-[#26334d]">Feasibility / contingency deadline</span>
                    <input
                      type="date"
                      value={feasibilityDeadline}
                      onChange={(event) => setFeasibilityDeadline(event.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-[#d1d9e3] bg-white px-3 text-sm text-[#26334d] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-[#26334d]">Closing date</span>
                    <input
                      type="date"
                      value={closingDate}
                      onChange={(event) => setClosingDate(event.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-[#d1d9e3] bg-white px-3 text-sm text-[#26334d] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    />
                  </label>
                </div>
              ) : null}

              {dealStage === 'parcel_controlled' || dealStage === 'active_predevelopment' ? (
                <div className="mt-7 grid gap-4 rounded-2xl bg-[#f7fafc] p-5 md:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-medium text-[#26334d]">Target permit submission</span>
                    <input
                      type="date"
                      value={targetPermitSubmission}
                      onChange={(event) => setTargetPermitSubmission(event.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-[#d1d9e3] bg-white px-3 text-sm text-[#26334d] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-[#26334d]">Target groundbreak</span>
                    <input
                      type="date"
                      value={targetGroundbreak}
                      onChange={(event) => setTargetGroundbreak(event.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-[#d1d9e3] bg-white px-3 text-sm text-[#26334d] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    />
                  </label>
                </div>
              ) : null}

              <div className="mt-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded-lg border border-[#d9e2ec] bg-white px-4 py-2.5 text-sm font-medium text-[#526b84] transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
                >
                  Back
                </button>
                <button
                  type="button"
                onClick={() => {
                  recordV2FunnelStep('v2_onboarding_step_deal_context', 'success', {
                    deal_stage: dealStage,
                    has_dates: Object.keys(dealContextDates).length > 0,
                  });
                  setStep(4);
                }}
                className="rounded-lg bg-[#101830] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#18233f] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
              >
                Continue to Priorities
                </button>
              </div>
            </section>

            <section className="rounded-2xl bg-[#101830] p-8 text-white shadow-[0px_18px_40px_rgba(16,24,48,0.12)]">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/55">Selected paths</p>
              <div className="mt-5 space-y-3">
                {selectedDevelopmentPathOptions.map((option) => (
                  <div key={option.key} className="rounded-xl bg-white/10 px-4 py-3">
                    <p className="text-sm font-semibold">{option.title}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm leading-6 text-white/70">
                Deal stage changes what Sela treats as urgent: fast screening, pre-offer diligence, contingency
                deadlines, permit readiness, or active coordination.
              </p>
            </section>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
            <section className="rounded-2xl bg-white p-8 shadow-[0px_18px_40px_rgba(16,24,48,0.06)]">
              <h1 className="text-[32px] font-bold tracking-[-0.04em] text-[#101830]">What matters most for this deal?</h1>
              <p className="mt-3 max-w-3xl text-[15px] leading-7 text-[#708099]">
                Choose up to 3 priorities. Sela will use these to rank risks, development paths, and next steps.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {PRIORITY_OPTIONS.map((option) => {
                  const selected = priorities.includes(option.key);
                  const disabled = !selected && priorities.length >= 3;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => togglePriority(option.key)}
                      className={`rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2 ${
                        selected
                          ? 'border-[#1abf7d] bg-[#f0fbf5]'
                          : 'border-[#e6ebf2] bg-white hover:border-[#c7d4e2]'
                      } ${disabled ? 'cursor-not-allowed opacity-45' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <h2 className="text-lg font-semibold text-[#101830]">{option.title}</h2>
                        <span className={`h-5 w-5 rounded border ${selected ? 'border-[#1abf7d] bg-[#1abf7d]' : 'border-[#c7d4e2] bg-white'}`} />
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="mt-4 text-sm text-[#8a98ad]">
                {priorities.length === 0
                  ? 'No priorities selected. Sela will use a balanced evaluation.'
                  : `${priorities.length} of 3 priorities selected.`}
              </p>

              {error ? <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div> : null}

              <div className="mt-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="rounded-lg border border-[#d9e2ec] bg-white px-4 py-2.5 text-sm font-medium text-[#526b84] transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#1abf7d] focus:ring-offset-2"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreateProject()}
                  disabled={creating}
                  className="rounded-lg bg-[#101830] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#aeb8c8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                >
                  {creating ? 'Creating workspace...' : 'Create evaluation workspace'}
                </button>
              </div>
            </section>

            <section className="rounded-2xl bg-white p-8 shadow-[0px_18px_40px_rgba(16,24,48,0.06)]">
              <p className="text-sm font-medium text-[#708099]">Evaluation workspace preview</p>
              <h2 className="mt-2 text-2xl font-semibold text-[#101830]">
                {locationMode === 'parcel'
                  ? `Parcel ${parcelPin.trim()} (${selectedParcelCity.label})`
                  : (selectedSuggestion || address).split(',')[0] || selectedSuggestion || address}
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#708099]">
                Sela will create a parcel evaluation workspace for this possible deal, not a finalized construction project.
              </p>

              <div className="mt-8 space-y-5 text-sm">
                <div>
                  <p className="font-semibold text-[#26334d]">Development paths</p>
                  <p className="mt-1 text-[#5f7088]">
                    {selectedDevelopmentPathOptions.map((option) => option.title).join(', ')}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-[#26334d]">Deal context</p>
                  <p className="mt-1 text-[#5f7088]">{selectedDealStage.title}</p>
                </div>
                <div>
                  <p className="font-semibold text-[#26334d]">Priorities</p>
                  <p className="mt-1 text-[#5f7088]">
                    {selectedPriorityOptions.length > 0
                      ? selectedPriorityOptions.map((option) => option.title).join(', ')
                      : 'Balanced evaluation'}
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
